import dayjs from 'dayjs';
import type { EnrichedWorklistItem } from '../../../helpers/api/dicom/dicom';

export type MergedWorklistKind = 'local' | 'mwl_pending';

export type PendingMwlRow = {
  kind: 'mwl_pending';
  key: string;
  mwlItem: EnrichedWorklistItem;
  startTime: string | null;
  patientName: string;
  patientId: string;
  accession: string | null;
  procedure: string;
  procedureId: string | null;
  modality: string | null;
  stationName: string | null;
  stationAe: string | null;
  location: string | null;
  requestingPhysician: string | null;
  priority: string | null;
  birthDate: string | null;
  sex: string | null;
  phone: string | null;
  email: string | null;
  facilityId: string | null;
  facilityName: string | null;
  divisionName: string | null;
  belongsToSelectedFacility: boolean | null;
  facilityCreated: boolean;
};

function sps0(item: EnrichedWorklistItem) {
  const seq = item.mwl?.ScheduledProcedureStepSequence;
  if (Array.isArray(seq) && seq.length > 0 && seq[0]) return seq[0];
  return {};
}

export function mwlItemKey(item: EnrichedWorklistItem): string {
  const mwl = item.mwl || {};
  return (
    mwl.AccessionNumber ||
    mwl.StudyInstanceUID ||
    `${mwl.PatientID}-${mwl.RequestedProcedureID || ''}-${JSON.stringify(
      sps0(item)
    )}`
  );
}

export function formatDicomPn(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const parts = raw.split('^');
  if (parts.length >= 3 && parts[0] && !/^[A-Za-z]/.test(parts[0])) {
    const last = parts[1] || '';
    const first = parts[2] || '';
    return [first, last].filter(Boolean).join(' ').trim() || raw;
  }
  if (parts.length >= 2) {
    return [parts[1], parts[0]].filter(Boolean).join(' ').trim() || raw;
  }
  return raw.trim();
}

export function parseMwlPhone(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const parts = raw.split('^');
  const first = parts[0]?.trim() || '';
  if (first && !first.includes('@')) return first;
  return null;
}

export function parseMwlEmail(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  for (const part of raw.split('^')) {
    const candidate = part?.trim() || '';
    if (candidate.includes('@') && candidate.split('@').pop()?.includes('.')) {
      return candidate;
    }
  }
  return null;
}

export function pendingRowFromMwl(item: EnrichedWorklistItem): PendingMwlRow {
  const mwl = item.mwl || {};
  const sps = sps0(item) as Record<string, string>;
  const d = String(sps.ScheduledProcedureStepStartDate || '').trim();
  const t = String(sps.ScheduledProcedureStepStartTime || '000000').trim();
  let startTime: string | null = null;
  if (d.length >= 8) {
    const hh = t.slice(0, 2) || '00';
    const mm = t.slice(2, 4) || '00';
    const ss = t.slice(4, 6) || '00';
    startTime = dayjs(
      `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}T${hh}:${mm}:${ss}`
    ).toISOString();
  }
  const pn = formatDicomPn(mwl.PatientName) || mwl.PatientName || '—';
  const procedure =
    (mwl.RequestedProcedureDescription || '').trim() ||
    (sps.ScheduledProcedureStepDescription || '').trim() ||
    '—';

  return {
    kind: 'mwl_pending',
    key: mwlItemKey(item),
    mwlItem: item,
    startTime,
    patientName: pn,
    patientId: mwl.PatientID || '—',
    accession: mwl.AccessionNumber || null,
    procedure,
    procedureId: mwl.RequestedProcedureID || null,
    modality: sps.Modality || null,
    stationName: sps.ScheduledStationName || null,
    stationAe: sps.ScheduledStationAETitle || null,
    location: sps.ScheduledProcedureStepLocation || null,
    requestingPhysician: formatDicomPn(mwl.RequestingPhysician),
    priority: mwl.RequestedProcedurePriority || null,
    birthDate: mwl.PatientBirthDate || null,
    sex: mwl.PatientSex || null,
    phone: parseMwlPhone(mwl.PatientTelephoneNumbers),
    email: parseMwlEmail(mwl.PatientTelephoneNumbers),
    facilityId: item.resolution?.facility_id || null,
    facilityName: item.resolution?.facility_name || null,
    divisionName: item.resolution?.division_name || null,
    belongsToSelectedFacility:
      item.resolution?.belongs_to_selected_facility ?? null,
    facilityCreated: Boolean(item.resolution?.facility_created),
  };
}

export function formatMwlBirthDate(raw?: string | null): string {
  if (!raw || raw.length < 8) return '—';
  return dayjs(
    `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`
  ).format('DD MMM YYYY');
}
